import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next'; // Import hook i18n

const InternationalJoinConference: React.FC = () => {
    const { t } = useTranslation('public'); // Gunakan namespace 'public'

    const listSpeakers = [
        {
            name: 'Prof. Dr. Hariyono, M.Pd.',
            organization: 'Universitas Negeri Malang, Indonesia',
            image: '/keynote-speakers/hariyono.jpg',
        },
        {
            name: 'Prof. Dr. Ni Nyoman Padmadewi, M.A.',
            organization: 'Universitas Pendidikan Ganesha, Indonesia',
            image: '/keynote-speakers/padmadewi.jpg',
        },
        {
            name: 'Prof. Sarah Prestridge',
            organization: 'Griffith University, Australia',
            image: '/keynote-speakers/sarah-prestridge.jpg',
        },
        {
            name: 'Prof. Dr. Abu Yazid Abu Bakar',
            organization: 'University Kebangsaan Malaysia, Malaysia',
            image: '/keynote-speakers/abu-yazid.jpeg',
        },
    ];

    return (
        <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30 dark:from-slate-900 dark:via-blue-950/10 dark:to-indigo-950/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-slate-100 dark:bg-grid-slate-800 opacity-30"></div>
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl"></div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                            {t('conference_joint.badge')}
                        </div>
                        <h2 className="text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                            International Joint Conference
                            <br />
                            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                On Global Knowledge Convergence <br />(IJ-GKC)
                            </span>
                        </h2>
                        <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                            {t('conference_joint.tagline')}
                        </p>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="text-center mb-16"
                        >
                            <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-amber-600 bg-clip-text text-transparent">
                                {t('conference_joint.keynote_title')}
                            </h2>
                            <p className="text-xl text-gray-600 dark:text-gray-300 mx-auto">
                                {t('conference_joint.keynote_desc')}
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                            {listSpeakers.map((speaker) => (
                                <motion.div
                                    key={speaker.name}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.1 }}
                                    className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                                >
                                    <div className="relative">
                                        <img
                                            src={speaker.image}
                                            alt={speaker.name}
                                            className="w-full h-64 object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                    </div>
                                    <div className="p-6">
                                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2">{speaker.name}</h4>
                                        <p className="text-gray-600 dark:text-gray-400 mb-4 text-xs">{speaker.organization}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
                        <div className="space-y-8">
                            {/* Feature 1 */}
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-primary"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-foreground mb-2">
                                        {t('conference_joint.features.upload.title')}
                                    </h3>
                                    <p className="text-muted-foreground">
                                        {t('conference_joint.features.upload.desc')}
                                    </p>
                                </div>
                            </div>

                            {/* Feature 2 */}
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-secondary"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-foreground mb-2">
                                        {t('conference_joint.features.discussion.title')}
                                    </h3>
                                    <p className="text-muted-foreground">
                                        {t('conference_joint.features.discussion.desc')}
                                    </p>
                                </div>
                            </div>

                            {/* Feature 3 */}
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-amber-600"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-foreground mb-2">
                                        {t('conference_joint.features.collaboration.title')}
                                    </h3>
                                    <p className="text-muted-foreground">
                                        {t('conference_joint.features.collaboration.desc')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="aspect-video w-full rounded-3xl overflow-hidden bg-slate-900 shadow-2xl">
                                <iframe
                                    className="w-full h-full"
                                    src="https://www.youtube.com/embed/Q36W41jo1oY?si=3NOnGRMU0AjSq8yp"
                                    title="KONASPI XII Platform Demo"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default InternationalJoinConference;